import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import UpdateIcon from '@mui/icons-material/Update';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import './ActivityTimeline.css';

const ActivityTimeline = ({ employees, employeeWorkload, workOrders }) => {
  // Generate timeline activities
  const activities = useMemo(() => {
    const events = [];

    // 1. New Hires (mock data - employees created recently)
    // In a real app, you'd have createdAt timestamps
    const recentEmployees = employees.slice(-3).reverse();
    recentEmployees.forEach((emp, index) => {
      const empName = emp.name || `${emp.empObject?.firstName || ''} ${emp.empObject?.lastName || ''}`.trim() || 'Unknown Employee';
      events.push({
        type: 'new_hire',
        icon: <PersonAddIcon />,
        color: '#10B981',
        title: 'New Employee',
        description: `${empName} joined as ${emp.empObject?.empDesignation || 'Employee'}`,
        time: `${(index + 1) * 2} days ago`,
        timestamp: Date.now() - (index + 1) * 2 * 24 * 60 * 60 * 1000
      });
    });

    // 2. Performance Milestones
    const employeesWithWorkload = employees
      .map(emp => ({
        ...emp,
        workload: employeeWorkload[emp.id] || 0
      }))
      .filter(emp => emp.workload > 0)
      .sort((a, b) => b.workload - a.workload);

    if (employeesWithWorkload.length > 0) {
      const topPerformer = employeesWithWorkload[0];
      if (topPerformer.workload >= 10) {
        const empName = topPerformer.name || `${topPerformer.empObject?.firstName || ''} ${topPerformer.empObject?.lastName || ''}`.trim() || 'Employee';
        events.push({
          type: 'milestone',
          icon: <EmojiEventsIcon />,
          color: '#F59E0B',
          title: 'Performance Milestone',
          description: `${empName} completed ${topPerformer.workload} routes`,
          time: '1 day ago',
          timestamp: Date.now() - 1 * 24 * 60 * 60 * 1000
        });
      }
    }

    // 3. Completed Routes
    const completedWorkOrders = workOrders
      .filter(wo => wo.workOrder?.status === 'completed')
      .slice(-5);

    completedWorkOrders.forEach((wo, index) => {
      const employee = employees.find(emp => emp.id === wo.workOrder?.employeeId);
      if (employee) {
        const empName = employee.name || `${employee.empObject?.firstName || ''} ${employee.empObject?.lastName || ''}`.trim() || 'Employee';
        events.push({
          type: 'completed_route',
          icon: <CheckCircleIcon />,
          color: '#2196F3',
          title: 'Route Completed',
          description: `${empName} completed service route`,
          time: `${(index + 1)} hours ago`,
          timestamp: Date.now() - (index + 1) * 60 * 60 * 1000
        });
      }
    });

    // 4. Status Changes (mock - high performers)
    const avgWorkload = employeesWithWorkload.length > 0
      ? employeesWithWorkload.reduce((sum, emp) => sum + emp.workload, 0) / employeesWithWorkload.length
      : 0;

    const highPerformers = employeesWithWorkload.filter(emp => emp.workload > avgWorkload * 1.2).slice(0, 2);
    highPerformers.forEach((emp, index) => {
      const empName = emp.name || `${emp.empObject?.firstName || ''} ${emp.empObject?.lastName || ''}`.trim() || 'Employee';
      events.push({
        type: 'status_change',
        icon: <UpdateIcon />,
        color: '#8B5CF6',
        title: 'Status Update',
        description: `${empName} promoted to high performer status`,
        time: `${(index + 1) * 3} days ago`,
        timestamp: Date.now() - (index + 1) * 3 * 24 * 60 * 60 * 1000
      });
    });

    // 5. Workload Updates
    const heavyWorkloadEmployees = employeesWithWorkload.filter(emp => emp.workload > 15).slice(0, 1);
    heavyWorkloadEmployees.forEach(emp => {
      const empName = emp.name || `${emp.empObject?.firstName || ''} ${emp.empObject?.lastName || ''}`.trim() || 'Employee';
      events.push({
        type: 'workload_update',
        icon: <TrendingUpIcon />,
        color: '#EF4444',
        title: 'Workload Alert',
        description: `${empName} reached ${emp.workload} routes (Heavy workload)`,
        time: '12 hours ago',
        timestamp: Date.now() - 12 * 60 * 60 * 1000
      });
    });

    // Sort by timestamp (most recent first)
    return events.sort((a, b) => b.timestamp - a.timestamp).slice(0, 10);
  }, [employees, employeeWorkload, workOrders]);

  if (activities.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 3, mb: 3 }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          color: '#003366',
          mb: 2,
          fontSize: { xs: '1.25rem', sm: '1.5rem' }
        }}
      >
        Recent Activity
      </Typography>

      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          maxHeight: { xs: 400, sm: 500 },
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#888',
            borderRadius: '10px',
            '&:hover': {
              backgroundColor: '#555',
            },
          },
        }}
      >
        <Timeline
          sx={{
            padding: 0,
            margin: 0,
            '& .MuiTimelineItem-root': {
              minHeight: { xs: 60, sm: 80 }
            }
          }}
        >
          {activities.map((activity, index) => (
            <TimelineItem key={index}>
              <TimelineOppositeContent
                sx={{
                  flex: 0.2,
                  paddingRight: { xs: 1, sm: 2 },
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  color: 'text.secondary',
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                {new Date(activity.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </TimelineOppositeContent>

              <TimelineSeparator>
                <TimelineDot
                  sx={{
                    backgroundColor: activity.color,
                    boxShadow: `0 0 0 4px ${activity.color}20`,
                    padding: { xs: 1, sm: 1.5 }
                  }}
                >
                  {React.cloneElement(activity.icon, {
                    sx: { fontSize: { xs: 16, sm: 20 }, color: 'white' }
                  })}
                </TimelineDot>
                {index < activities.length - 1 && <TimelineConnector />}
              </TimelineSeparator>

              <TimelineContent sx={{ paddingLeft: { xs: 1, sm: 2 }, paddingRight: 0 }}>
                <Paper
                  elevation={1}
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    backgroundColor: '#f8f9fa',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: '#e9ecef',
                      boxShadow: 2
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: '#003366',
                        fontSize: { xs: '0.85rem', sm: '0.95rem' }
                      }}
                    >
                      {activity.title}
                    </Typography>
                    <Chip
                      label={activity.type.replace('_', ' ')}
                      size="small"
                      sx={{
                        backgroundColor: `${activity.color}20`,
                        color: activity.color,
                        fontWeight: 600,
                        fontSize: { xs: '0.6rem', sm: '0.7rem' },
                        height: { xs: 18, sm: 20 },
                        display: { xs: 'none', sm: 'flex' }
                      }}
                    />
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}
                  >
                    {activity.description}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                      display: { xs: 'block', sm: 'none' },
                      mt: 0.5
                    }}
                  >
                    {new Date(activity.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </Typography>
                </Paper>
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </Paper>
    </Box>
  );
};

export default ActivityTimeline;
